import React, { useState } from 'react';
import { Category, ShirtSize, Athlete, RegistrationLevel, Team } from '../types';
import { CATEGORIES, SHIRT_SIZES, REGISTRATION_FEES } from '../constants';
import { maskCPF, maskDate, maskPhone } from '../utils';

interface AthleteFormProps {
  onAdd: (athlete: Omit<Athlete, 'id' | 'registrationFee' | 'shirtValue' | 'totalValue' | 'createdBy'>) => void;
  teams: Team[];
  defaultTeamId?: string;
  compact?: boolean;
  initialData?: any;
}

const AthleteForm: React.FC<AthleteFormProps> = ({ onAdd, teams, defaultTeamId = '', compact = false, initialData }) => {
  const [formData, setFormData] = useState({
    fullName: initialData?.fullName || '',
    cpf: initialData?.cpf || '',
    identity: initialData?.identity || initialData?.rg || '',
    phone: initialData?.phone || '',
    birthDate: initialData?.birthDate || '',
    category: initialData?.category || CATEGORIES[0],
    hasShirt: initialData?.hasShirt ?? true,
    shirtSize: initialData?.shirtSize || SHIRT_SIZES[1],
    registrationLevel: (initialData?.registrationLevel || 1) as RegistrationLevel,
    teamId: initialData?.teamId || defaultTeamId,
  });

  React.useEffect(() => {
    if (initialData) {
      setFormData({
        fullName: initialData.fullName || '',
        cpf: initialData.cpf || '',
        identity: initialData.identity || initialData.rg || '',
        phone: initialData.phone || '',
        birthDate: initialData.birthDate || '',
        category: initialData.category || CATEGORIES[0],
        hasShirt: initialData.hasShirt ?? true,
        shirtSize: initialData.shirtSize || SHIRT_SIZES[1],
        registrationLevel: (initialData.registrationLevel || 1) as RegistrationLevel,
        teamId: initialData.teamId || defaultTeamId,
      });
    }
  }, [initialData, defaultTeamId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName || !formData.cpf || !formData.birthDate || !formData.phone || !formData.identity) {
      alert("Por favor, preencha todos os campos obrigatórios.");
      return;
    }
    onAdd(formData);
    setFormData({
      fullName: '',
      cpf: '',
      identity: '',
      phone: '',
      birthDate: '',
      category: CATEGORIES[0],
      hasShirt: true,
      shirtSize: SHIRT_SIZES[1],
      registrationLevel: 1,
      teamId: defaultTeamId,
    });
  };

  const inputClass = "border border-slate-200 rounded-xl p-3 text-sm focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all w-full font-medium bg-white text-slate-900 placeholder:text-slate-300";

  return (
    <form onSubmit={handleSubmit} className={`${compact ? '' : 'bg-white p-8 rounded-[2.5rem] shadow-xl border border-slate-100 mb-8'}`}>
      {!compact && (
        <div className="mb-8">
          <h2 className="text-2xl font-black text-slate-800 flex items-center gap-3">
            <span className="bg-blue-600 text-white w-10 h-10 rounded-2xl flex items-center justify-center text-sm shadow-lg shadow-blue-100">02</span>
            Dados do Atleta
          </h2>
        </div>
      )}
      
      <div className={`grid grid-cols-1 ${compact ? 'gap-4' : 'md:grid-cols-2 lg:grid-cols-3 gap-6'}`}>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Nome Completo *</label>
          <input type="text" required placeholder="Nome completo" className={inputClass} value={formData.fullName} onChange={(e) => setFormData({ ...formData, fullName: e.target.value })} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Identidade (RG) *</label>
          <input type="text" required placeholder="Nº da Identidade" className={inputClass} value={formData.identity} onChange={(e) => setFormData({ ...formData, identity: e.target.value })} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">CPF *</label>
          <input type="text" required placeholder="000.000.000-00" className={inputClass} value={formData.cpf} onChange={(e) => setFormData({ ...formData, cpf: maskCPF(e.target.value) })} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">WhatsApp *</label>
          <input type="text" required placeholder="(00) 00000-0000" className={inputClass} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: maskPhone(e.target.value) })} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Data de Nasc. *</label>
          <input type="text" required placeholder="DD/MM/AAAA" className={inputClass} value={formData.birthDate} onChange={(e) => setFormData({ ...formData, birthDate: maskDate(e.target.value) })} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Categoria</label>
          <select className={inputClass} value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value as Category })}>
            {CATEGORIES.map((cat) => ( <option key={cat} value={cat}>{cat}</option> ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Camisa Oficial?</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setFormData({ ...formData, hasShirt: true })} className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest border-2 transition-all ${formData.hasShirt ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-100' : 'bg-white text-slate-400 border-slate-100 hover:border-slate-200'}`}>Sim (+R$60)</button>
            <button type="button" onClick={() => setFormData({ ...formData, hasShirt: false })} className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest border-2 transition-all ${!formData.hasShirt ? 'bg-slate-800 text-white border-slate-800 shadow-lg' : 'bg-white text-slate-400 border-slate-100 hover:border-slate-200'}`}>Não</button>
          </div>
        </div>
        <div className={`flex flex-col gap-1.5 transition-all ${formData.hasShirt ? 'opacity-100' : 'opacity-40 grayscale pointer-events-none'}`}>
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Tamanho da Camisa</label>
          <select className={inputClass} value={formData.shirtSize} onChange={(e) => setFormData({ ...formData, shirtSize: e.target.value as ShirtSize })} disabled={!formData.hasShirt}>
            {SHIRT_SIZES.map((size) => ( <option key={size} value={size}>Tamanho {size}</option> ))}
          </select>
        </div>
      </div>

      <div className="mt-10 border-t border-slate-50 pt-8">
        <label className="text-[10px] font-black text-slate-500 block mb-4 uppercase tracking-widest px-1">Qual o lote desta inscrição?</label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((lvl) => {
            const level = lvl as RegistrationLevel;
            const isActive = formData.registrationLevel === level;
            return (
              <label key={lvl} className={`flex items-center justify-between p-4 rounded-2xl border-2 cursor-pointer transition-all ${isActive ? 'border-blue-600 bg-blue-50/50' : 'border-slate-100 bg-white'}`}>
                <div className="flex items-center gap-4">
                  <input type="radio" name="regLevel" className="hidden" checked={isActive} onChange={() => setFormData({ ...formData, registrationLevel: level })} />
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isActive ? 'border-blue-600' : 'border-slate-300'}`}>
                    {isActive && <div className="w-2.5 h-2.5 rounded-full bg-blue-600"></div>}
                  </div>
                  <span className="text-xs font-black uppercase">{lvl}ª Inscrição</span>
                </div>
                <span className="text-sm font-black text-blue-600">{REGISTRATION_FEES[level] === 0 ? 'Isento' : `R$ ${REGISTRATION_FEES[level]}`}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <button type="submit" className="w-full md:w-auto md:px-16 bg-blue-600 text-white font-black py-5 rounded-2xl shadow-xl hover:bg-blue-700 transition-all flex items-center justify-center gap-3 active:scale-95 text-sm uppercase tracking-widest">
          {initialData ? 'Salvar Alterações' : 'Concluir Inscrição'}
        </button>
      </div>
    </form>
  );
};

export default AthleteForm;